import {
  PlusCircle,
  XCircle,
  FileWarning,
  PauseCircle,
  Factory,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { JustifyEventDialog } from "@/features/machine-sidebar/JustifyEventDialog";
import { useLineScrapByMachine, useLineTrackedByMachine } from "@/hooks/useLineTrackedByMachine";
import { QtyForm } from "./forms/QtyForm";
import { LossForm } from "./forms/LossForm";
import { PauseObsForm } from "./forms/PauseObsForm";
import { useOperationalEntryState } from "./state";
import type { ActiveRun } from "./types";

interface Props {
  lineId: string;
  activeRun: ActiveRun;
}

export function OperationalEntryButtons({ lineId, activeRun }: Props) {
  const {
    openAction,
    setOpenAction,
    closeAction,
    justifyEventId,
    setJustifyEventId,
    pendingEvents,
    isPaused,
    isError,
    refetchEvents,
  } = useOperationalEntryState(lineId, activeRun);
  // No modo por-máquina a produção E o refugo são reportados na máquina;
  // as entradas de quantidade e perda no nível da linha somem (1 só escritor; caminho único).
  const trackedByMachine = useLineTrackedByMachine(lineId);
  // Spec 081 — o refugo tem a SUA pergunta (`lineHasScrapCounter`: PRODUCES ou SCRAPS), que é a
  // mesma que a rota faz. Com a pergunta da produção, uma linha só de contadores de consumo
  // continuava oferecendo "Registrar perda" e recebia 409 em todo envio.
  const scrapByMachine = useLineScrapByMachine(lineId);

  if (openAction === "quantity" && !trackedByMachine) {
    return <QtyForm lineId={lineId} onDone={closeAction} />;
  }
  if (openAction === "loss" && !scrapByMachine) {
    return <LossForm lineId={lineId} onDone={closeAction} />;
  }
  if (openAction === "pause-obs") {
    return <PauseObsForm lineId={lineId} runId={activeRun.id} onDone={closeAction} />;
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {trackedByMachine ? (
          <span className="inline-flex items-center gap-1.5 text-xs italic text-muted-foreground">
            <Factory className="h-4 w-4" />
            Produção registrada por máquina
          </span>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpenAction("quantity")}
            disabled={isPaused}
          >
            <PlusCircle className="h-4 w-4" />
            Registrar quantidade
          </Button>
        )}
        {scrapByMachine ? (
          <span className="inline-flex items-center gap-1.5 text-xs italic text-muted-foreground">
            <Factory className="h-4 w-4" />
            Refugo registrado por máquina
          </span>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setOpenAction("loss")}>
            <XCircle className="h-4 w-4" />
            Registrar perda
          </Button>
        )}
        {isError ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetchEvents()}
            className="border-destructive/40 text-destructive"
          >
            <AlertTriangle className="h-4 w-4" />
            Falha ao carregar eventos — tentar novamente
          </Button>
        ) : pendingEvents.length > 0 ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setJustifyEventId(pendingEvents[0]!.id)}
            className="border-rd-warning-500/40 text-rd-warning-700"
          >
            <FileWarning className="h-4 w-4" />
            Justificar evento ({pendingEvents.length})
          </Button>
        ) : null}
        {!isPaused ? (
          <Button variant="outline" size="sm" onClick={() => setOpenAction("pause-obs")}>
            <PauseCircle className="h-4 w-4" />
            Pausar com observação
          </Button>
        ) : (
          <p className="text-xs italic text-muted-foreground">
            Produção pausada — use a barra de comandos para retomar/finalizar.
          </p>
        )}
      </div>

      <JustifyEventDialog
        eventId={justifyEventId}
        open={!!justifyEventId}
        onClose={() => setJustifyEventId(null)}
      />
    </>
  );
}
